import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { AlertTriangle, AlertCircle, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

const DISMISS_KEY = "api_quota_alerts_dismissed_at";
const DISMISS_TTL_MS = 60 * 60 * 1000; // 1 hour

const SEVERITY_STYLE = {
  error:    { ring: "border-rose-300 bg-rose-50 text-rose-900 dark:bg-rose-950/40 dark:text-rose-100 dark:border-rose-900/60", icon: AlertCircle, iconClass: "text-rose-600" },
  over:     { ring: "border-rose-300 bg-rose-50 text-rose-900 dark:bg-rose-950/40 dark:text-rose-100 dark:border-rose-900/60", icon: AlertCircle, iconClass: "text-rose-600" },
  critical: { ring: "border-rose-300 bg-rose-50 text-rose-900 dark:bg-rose-950/40 dark:text-rose-100 dark:border-rose-900/60", icon: AlertCircle, iconClass: "text-rose-600" },
  warning:  { ring: "border-amber-300 bg-amber-50 text-amber-900 dark:bg-amber-950/40 dark:text-amber-100 dark:border-amber-900/60", icon: AlertTriangle, iconClass: "text-amber-600" },
};

function buildMessage(alert, t) {
  if (alert.severity === "error") {
    return t("api_alerts.msg_error", { name: alert.name, error: alert.message || "" });
  }
  if (alert.severity === "over") {
    return t("api_alerts.msg_over", { name: alert.name, total: alert.total });
  }
  if (alert.severity === "critical") {
    if (alert.remaining != null && alert.total != null) {
      return t("api_alerts.msg_critical_quota", {
        name: alert.name, remaining: alert.remaining, total: alert.total, pct: alert.remaining_pct,
      });
    }
    return t("api_alerts.msg_critical_credits", { name: alert.name, remaining: alert.remaining });
  }
  // warning
  if (alert.remaining != null && alert.total != null) {
    return t("api_alerts.msg_warning_quota", {
      name: alert.name, remaining: alert.remaining, total: alert.total, pct: alert.remaining_pct,
    });
  }
  return t("api_alerts.msg_warning_credits", { name: alert.name, remaining: alert.remaining });
}

export default function ApiQuotaBanner() {
  const { user } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [alerts, setAlerts] = useState([]);
  const [hidden, setHidden] = useState(false);

  const isAdmin = user && user.role === "ADMIN";

  useEffect(() => {
    if (!isAdmin) return;
    (async () => {
      try {
        const { data } = await api.get("/admin/api-credits/alerts");
        const list = data?.alerts || [];
        setAlerts(list);

        // Toast logic — debounce per (provider, severity) for 1h via localStorage
        if (list.length > 0) {
          const raw = localStorage.getItem(DISMISS_KEY);
          const dismissed = raw ? JSON.parse(raw) : {};
          const now = Date.now();
          const toToast = list.filter(a => {
            const key = `${a.id}:${a.severity}`;
            const last = dismissed[key];
            return !last || (now - last) > DISMISS_TTL_MS;
          });
          if (toToast.length > 0) {
            const updated = { ...dismissed };
            toToast.forEach(a => {
              const key = `${a.id}:${a.severity}`;
              const fn = (a.severity === "warning") ? toast.warning : toast.error;
              fn(buildMessage(a, t), {
                description: t("api_alerts.toast_description"),
                action: {
                  label: t("api_alerts.view"),
                  onClick: () => navigate("/credits"),
                },
                duration: 8000,
              });
              updated[key] = now;
            });
            localStorage.setItem(DISMISS_KEY, JSON.stringify(updated));
          }
        }
      } catch {
        // silent: alerts are nice-to-have, never block dashboard
      }
    })();
  }, [isAdmin, navigate, t]);

  if (!isAdmin || hidden || alerts.length === 0) return null;

  const top = alerts[0];
  const style = SEVERITY_STYLE[top.severity] || SEVERITY_STYLE.warning;
  const Icon = style.icon;

  return (
    <div
      className={`mb-4 rounded-lg border ${style.ring} px-4 py-3 flex items-start gap-3`}
      data-testid="api-quota-banner"
    >
      <Icon className={`h-4 w-4 mt-0.5 shrink-0 ${style.iconClass}`} />
      <div className="flex-1 min-w-0">
        <div className="text-sm font-medium" data-testid="api-quota-banner-message">
          {buildMessage(top, t)}
        </div>
        {alerts.length > 1 && (
          <div className="text-[11px] mt-0.5 opacity-80">
            {t("api_alerts.more_count", { count: alerts.length - 1 })}
          </div>
        )}
      </div>
      <Button
        size="sm" variant="outline"
        className="h-7 text-[11px] bg-transparent"
        onClick={() => navigate("/credits")}
        data-testid="api-quota-banner-view"
      >
        {t("api_alerts.view")}
      </Button>
      <button
        onClick={() => setHidden(true)}
        className="opacity-60 hover:opacity-100 shrink-0"
        title={t("api_alerts.dismiss")}
        data-testid="api-quota-banner-dismiss"
      >
        <X className="h-4 w-4" />
      </button>
    </div>
  );
}
